import { create } from 'zustand';
import { categoryService, CreateCategoryDto, UpdateCategoryDto, GetPaginationCategoriesDto, Category } from '@/services/categoryService';

interface CategoryState {
    categories: Category[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    roots: Category[];
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    error: string | null;

    fetchCategories: (query?: GetPaginationCategoriesDto) => Promise<void>;
    fetchRoots: () => Promise<void>;
    fetchCategoryById: (id: string) => Promise<Category | null>;
    createCategory: (data: CreateCategoryDto) => Promise<boolean>;
    updateCategory: (id: string, data: UpdateCategoryDto) => Promise<boolean>;
    deleteCategory: (id: string) => Promise<boolean>;
}

export const useCategoryStore = create<CategoryState>()((set, get) => ({
    categories: [],
    pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
    roots: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,

    fetchCategories: async (query) => {
        set({ isLoading: true, error: null });
        try {
            const res = await categoryService.findAll(query || { page: get().pagination.page, limit: get().pagination.limit });
            if (res.ok && res.data) {
                const data: any = res.data.data;

                set({
                    categories: data?.data?.categories || [],
                    pagination: data?.data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
                    isLoading: false,
                });
            } else {
                set({ isLoading: false, error: (res as any)?.data?.message || 'Lỗi khi tải danh mục' });
            }
        } catch (e) {
            set({ isLoading: false, error: e instanceof Error ? e.message : 'Lỗi khi tải danh mục' });
        }
    },

    fetchCategoryById: async (id) => {
        try {
            const res = await categoryService.findOne(id);
            if (res.ok && res.data) {
                return res.data.data?.data as Category;
            }
            return null;
        } catch {
            return null;
        }
    },

    createCategory: async (data) => {
        set({ isCreating: true, error: null });
        try {
            const res = await categoryService.create(data);
            set({ isCreating: false });
            if (res.ok) {
                await get().fetchCategories();
                return true;
            }
            return false;
        } catch (e) {
            set({ isCreating: false, error: e instanceof Error ? e.message : 'Lỗi khi tạo danh mục' });
            return false;
        }
    },

    updateCategory: async (id, data) => {
        set({ isUpdating: true, error: null });
        try {
            const res = await categoryService.update(id, data);
            set({ isUpdating: false });
            if (res.ok) {
                await get().fetchCategories();
                return true;
            }
            return false;
        } catch (e) {
            set({ isUpdating: false, error: e instanceof Error ? e.message : 'Lỗi khi cập nhật danh mục' });
            return false;
        }
    },

    deleteCategory: async (id) => {
        set({ isDeleting: true, error: null });
        try {
            const res = await categoryService.remove(id);
            set({ isDeleting: false });
            if (res.ok) {
                await get().fetchCategories();
                return true;
            }
            return false;
        } catch (e) {
            set({ isDeleting: false, error: e instanceof Error ? e.message : 'Lỗi khi xóa danh mục' });
            return false;
        }
    },

    fetchRoots: async () => {
        try {
            const res = await categoryService.findRoots({ page: 1, limit: 100 });
            console.log("res: ", res)
            if (res.ok && res.data) {
                set({ roots: (res.data.data?.data?.categories as Category[]) || [] });
            }
        } catch {
            // ignore
        }
    },
}));
